import type {
	GetSubscriptionsTruemedCheckoutPaymentSessionIdResponseBody,
	PostSubscriptionsTruemedCheckoutRequestBody,
	PostSubscriptionsTruemedCheckoutResponseBody,
} from '@calm-web/api-sdk/lib/app-api';

import { ApiRequest } from '@/hooks/api';

export type TruemedPlan = 'monthly' | 'quarterly' | 'yearly';

export type TruemedErrorKind = 'already_subscribed' | 'not_enabled' | 'invalid' | 'unavailable' | 'unknown';

export const startTruemedCheckout = async (
	apiRequest: ApiRequest,
	body: PostSubscriptionsTruemedCheckoutRequestBody,
): Promise<PostSubscriptionsTruemedCheckoutResponseBody> => {
	const { data } = await apiRequest<PostSubscriptionsTruemedCheckoutResponseBody>({
		service: 'api',
		endpoint: 'subscriptions/truemed/checkout',
		method: 'POST',
		body,
		noCache: true,
	});
	return data;
};

export const fetchTruemedCheckoutStatus = async (
	apiRequest: ApiRequest,
	paymentSessionId: string,
): Promise<GetSubscriptionsTruemedCheckoutPaymentSessionIdResponseBody> => {
	const { data } = await apiRequest<GetSubscriptionsTruemedCheckoutPaymentSessionIdResponseBody>({
		service: 'api',
		// The id comes back from Truemed via a redirect query param, so it is caller-influenceable.
		endpoint: `subscriptions/truemed/checkout/${encodeURIComponent(paymentSessionId)}`,
		method: 'GET',
		noCache: true,
	});
	return data;
};

// `apiRequest` rejects with the axios response itself (status at the top level) for HTTP errors,
// but a raw axios error nests it under `response`; accept both shapes.
export const getHttpStatus = (error: unknown): number | undefined => {
	if (typeof error !== 'object' || error === null) {
		return undefined;
	}
	const { status, response } = error as { status?: unknown; response?: { status?: unknown } };
	const value = typeof status === 'number' ? status : response?.status;
	return typeof value === 'number' ? value : undefined;
};

type ErrorBody = { data?: { error?: { code?: unknown } } };

// Same two rejection shapes as `getHttpStatus`; the api body is `{ error: { code } }`.
export const getErrorCode = (error: unknown): string | undefined => {
	if (typeof error !== 'object' || error === null) {
		return undefined;
	}
	const { data, response } = error as ErrorBody & { response?: ErrorBody };
	const code = (data ?? response?.data)?.error?.code;
	return typeof code === 'string' ? code : undefined;
};

// Other 400s (missing email, bad failure path, unsupported plan) are not the buyer's ZIP, so
// telling them to fix it would send them in circles.
const ADDRESS_ERROR_CODES: readonly string[] = ['truemed_address_invalid', 'truemed_unsupported_country'];

export const classifyTruemedError = (error: unknown): TruemedErrorKind => {
	switch (getHttpStatus(error)) {
		case 409:
			return 'already_subscribed';
		case 403:
			return 'not_enabled';
		case 400: {
			const code = getErrorCode(error);
			return code !== undefined && ADDRESS_ERROR_CODES.includes(code) ? 'invalid' : 'unknown';
		}
		case 502:
		case 503:
			return 'unavailable';
		default:
			return 'unknown';
	}
};
